import { getParams } from '../../utils/searchParams';
import { FullscreenError } from '../../components/FullscreenError/FullscreenError';
import { Layout } from '../../components/Layout/Layout';
import { RequestDetails } from '../../components/RequestDetails/RequestDetails';
import { Loader } from '@repo/ui/Loader';
import { Flex } from '@repo/ui/Flex';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { QUERIES } from '../../utils/constants';
import { getClaimQueryFn } from '../../graphql/query/getClaim';
import type { GetOrderResult, Claim } from '../../graphql/query/getOrder';
import { PageWrap } from '../../components/Page/Page';
import { Promo } from '../../components/Promo/Promo';
import { formatToShort } from '../../utils/dates';
import { hasExplicitWidgetContext } from '../../components/RequestDetails/helpers';
import type { ClaimStatus } from '../../components/RequestDetails/types';

const getClaimStatus = (
  orderItems: GetOrderResult['order']['items'] | undefined,
  claimId: string,
): ClaimStatus | undefined => {
  if (!orderItems) return undefined;

  for (const item of orderItems) {
    const claim = item.claims?.find((c: Claim) => c.claimId === claimId);
    if (claim?.status) {
      return claim.status;
    }
  }
  return undefined;
};

export const ClaimDetails = () => {
  const params = getParams();
  const claimId = params?.claimId as string;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { retailer } = useParams({ strict: false });

  const {
    isError,
    isLoading,
    data: submittedRequestsData,
  } = useQuery({
    queryKey: [QUERIES.GET_CLAIM, claimId],
    queryFn: () => getClaimQueryFn(claimId),
    retry: false,
  });

  const orderData = queryClient.getQueryData<GetOrderResult>([
    QUERIES.GET_ORDER,
  ]);

  if (isError) {
    return (
      <PageWrap>
        <Flex grow="1" justifyContent="center" alignItems="center">
          <FullscreenError
            title={{
              langKey: [
                'Global.claimLookup.error.title',
                'Global.fullscreenError.title',
              ],
            }}
            description={{
              langKey: [
                'Global.claimLookup.error.description',
                'Global.fullscreenError.description',
              ],
            }}
          />
        </Flex>
      </PageWrap>
    );
  }

  if (isLoading || !submittedRequestsData) {
    return (
      <PageWrap>
        <Flex grow="1" justifyContent="center" alignItems="center">
          <Loader size="lg" label="Loading..." />
        </Flex>
      </PageWrap>
    );
  }

  const {
    submitDate,
    deliveryType,
    items,
    packagingOption,
    shipments,
    status,
    location,
    id: requestId,
  } = submittedRequestsData.claim;

  const order = orderData?.order;

  const email = order?.orderTokens?.find(
    (token) => token?.key === 'EMAIL',
  )?.value;

  const replacementTokens = Object.fromEntries(
    Object.entries({
      orderNumber: order?.orderNumber,
      submittedDate: submitDate
        ? formatToShort(submitDate, params?.locale)
        : undefined,
      deliveryMethodType: deliveryType,
      itemCount: items?.length ? `${items.length}` : undefined,
      email,
    }).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;

  // Only show email widget if explicitly configured as [[email]] in language keys
  const showEmailWidget = hasExplicitWidgetContext({
    type: deliveryType,
    packaging: packagingOption,
    widgetType: 'email',
  });

  const handleBack = () => {
    navigate({
      to: '/$retailer/item-selection',
      params: { retailer: retailer ?? '' },
      search: params ?? {},
    });
  };

  const claimStatus = getClaimStatus(order?.items, claimId);

  return (
    <PageWrap>
      <Layout>
        <Promo id={'claimDetails.pageTop'} />
        <RequestDetails
          confirmationEmail={showEmailWidget}
          headerTitle={{
            langKey: [
              `Global.requestDetails.${claimStatus}.${deliveryType}.${packagingOption}.header.title`,
              `Global.requestDetails.${deliveryType}.${packagingOption}.header.title`,
              'Global.requestDetails.header.title',
            ],
            replacementTokens,
          }}
          headerSubtitle={{
            langKey: [
              `Global.requestDetails.${claimStatus}.${deliveryType}.${packagingOption}.header.subtitle`,
              `Global.requestDetails.${deliveryType}.${packagingOption}.header.subtitle`,
              'Global.requestDetails.header.subtitle',
            ],
            replacementTokens,
          }}
          data={{
            requestId,
            orderItems: order?.items ?? [],
            requestItems: items ?? [],
            deliveryType,
            deliveryMethod: { packagingOption },
            label: shipments?.[0]?.labelUrl
              ? { url: shipments[0].labelUrl }
              : null,
            qrCode: shipments?.[0]?.qrCodeUrl
              ? { url: shipments[0].qrCodeUrl }
              : null,
            status: status ?? 'REQUEST_COMPLETE',
            location,
            email,
            claimStatus,
            claimId,
          }}
          replacementTokens={replacementTokens}
          onBack={handleBack}
        >
          <Promo id={'claimDetails.pageBottom'} />
        </RequestDetails>
      </Layout>
    </PageWrap>
  );
};
